/* eslint-disable no-undef */
import {
  ZOOM,
  MIN_ZOOM,
  MAX_ZOOM,
  FIAS_ID
} from './const';
import {
  showMessage,
  logMessage,
  getResponseByMode,
  isNotHidden
} from './utils';
import {
  Points
} from './map/points';
import {
  FilterList,
  FilterSelect
} from './map/filter';
import {
  Tabs
} from '../src/tabs';
import {
  setPresets,
  installPresets
} from './map/preset';
import {
  getMyClasterContentLayout,
  getClusterIcons,
  clusterNumbers
} from './map/clusters';
import {
  setBaloonLayout,
  Baloon
} from './map/baloon';


export class YaMap {
  constructor(cls) {
    this._container = document.querySelector(cls);
    this._id = this._container.id;
    this._preload = this._container.querySelector('.map__loading');
    this._myMap = null;
    this._objectManager = null;
    this._points = null;
    this._filter = null;
    this._tabs = null;
    this._baloon = null;
    this._prevObjectId = null;
    this._count = 0;
    this._build = '';

    this._centerBalloon = this._centerBalloon.bind(this);
    this._filterHandler = this._filterHandler.bind(this);
    this._baloonClickHandler = this._baloonClickHandler.bind(this);
    this._handleBaloonClick = this._handleBaloonClick.bind(this);
  }

  getMap() {
    return this._myMap;
  }

  init() {
    return new Promise((resolve, reject) => {

      this._myMap = this._create();

      if (this._isMapWithMargin()) {
        this._setMargin();
      }
      this._build = this._getBuild();

      setPresets();

      this._objectManager = this._createObjectManager();

      this._objectManager.objects.events.add(['add'], installPresets.bind(this._objectManager));

      if (this._hasCustomBaloon()) {
        this._objectManager.objects.events.add('click', this._baloonClickHandler);
      } else {
        setBaloonLayout();
      }

      this._tabs = this._setTab();

      this._load(resolve, reject);
    });

  }

  fit() {
    const _centerAndZoom = this._centerAndZoom();

    let {
      center,
      zoom
    } = _centerAndZoom;

    if (this._count === 1) {
      zoom = MIN_ZOOM;
    }

    this._myMap.setCenter(center, zoom, {
      useMapMargin: true
    });
  }

  centerById(objectId) {
    this._tabs.show(0);
    this._focus(objectId);

    if (this._hasCustomBaloon()) {
      this._baloonByIdClickHandler(objectId);
    } else {
      this._objectManager.objects.balloon.open(objectId);
    }
  }

  reset() {
    this._destroyBaloon();
    this._setDefaultIcon(this._prevObjectId);
    this._prevObjectId = null;
    this._showList();
  }

  filterByCompany(company) {
    if (this._filterList) {
      this._checkAndSetCategory(this._filterList, company);
    }

    if (this._filterSelect) {
      this._checkAndSetCategory(this._filterSelect, company);
    }
  }

  _create() {
    return new ymaps.Map(this._id, {
      center: [66.413951, 94.241942],
      zoom: ZOOM,
      controls: ['zoomControl', 'geolocationControl']
    }, {
      suppressMapOpenBlock: true
    });
  }

  _setMargin() {
    this._myMap.margin.setDefaultMargin([50, 380, 15, 15]);
  }

  _setTab() {
    const tabContainer = this._container.closest('.tabs');
    if (tabContainer) {
      const cls = this._getTabClass(tabContainer);
      if (cls) {
        return new Tabs(cls);
      }
    }
    return null;
  }

  _getTabClass(elem) {
    return `.${Array.from(elem.classList).find((cls) => cls.includes('tabs--'))}`;
  }

  _createObjectManager() {
    return new ymaps.ObjectManager({
      clusterize: true,
      geoObjectOpenBalloonOnClick: !this._hasCustomBaloon(),
      clusterOpenBalloonOnClick: true,
      gridSize: 64,
      zoomMargin: [27, 32],
      maxZoom: (MAX_ZOOM - 1),
      clusterIcons: getClusterIcons(),
      clusterNumbers: clusterNumbers,
      clusterIconContentLayout: getMyClasterContentLayout()
    });
  }

  _load(resolve, reject) {
    const mock = `cache/checkout/8dea00e3-9aab-4d8e-887c-ef2aaa546456_contacts.json`;
    const real = `${window.location.origin}/cache/checkout/${FIAS_ID}_${this._build}.json`;

    const url = getResponseByMode(mock, real);

    return fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(response.status);
      })
      .then((data) => {
        this._count = data.features.length;
        this._objectManager.add(data);
        this._myMap.geoObjects.add(this._objectManager);
        this._addPoints(data);
        this._addFilter(data);
        this.fit();
        if (this._preload) {
          this._preload.remove();
        }
        resolve(this);
      })
      .catch((error) => {
        console.warn(error);
        logMessage(error);
        showMessage(`Не удалось загрузить карту`);
        reject(error);
      });
  }

  _addPoints(data) {
    if (this._hasPoints()) {

      new Points().init(data)
        .then((self) => {
          self.setListClickHandler(this._centerBalloon);
          this._points = self;
          this._list = this._setList();
        });

    }
  }

  _addFilter(data) {
    if (this._hasFilterList()) {
      this._filterList = new FilterList('.pickup-points__delivery', this._objectManager, data);
      this._filterList.setElemChangeHandler(this._filterHandler);
    }

    if (this._hasFilterSelect()) {
      this._filterSelect = new FilterSelect('.pickup-points__count', this._objectManager, data);
      this._filterSelect.setElemChangeHandler(this._filterHandler);
    }
  }

  _checkAndSetCategory(filter, company) {
    const elem = filter.getElem();
    const filterIsVisible = isNotHidden(elem);

    if (filterIsVisible) {
      filter.setCategory(company);
    }
  }

  _addBaloon(obj) {
    if (this._baloon) {
      this._destroyBaloon();
    }
    this._baloon = new Baloon(obj);
    this._baloon.setClickHandler(this._handleBaloonClick);

    this._insertBaloon();
  }

  _insertBaloon() {
    const baloon = this._baloon.getElem();
    this._container.append(baloon);
  }

  _destroyBaloon() {
    if (this._baloon) {
      this._baloon.destroy();
      this._baloon = null;
    }
  }

  _centerAndZoom() {
    return ymaps.util.bounds.getCenterAndZoom(this._myMap.geoObjects.getBounds(), this._myMap.container.getSize(), this._myMap.options.get('projection'));
  }

  _handleBaloonClick() {
    this.reset();
  }

  _filterHandler(category, filterFunction) {
    if (this._points) {
      this._points.filter(category);
    }
    this._objectManager.setFilter(filterFunction);
    this.fit();
  }

  _baloonClickHandler(e) {
    let objectId = e.get('objectId');

    this._baloonByIdClickHandler(objectId);
  }

  _baloonByIdClickHandler(objectId) {
    let obj = this._objectManager.objects.getById(objectId);

    this._setIcon(objectId);
    this._addBaloon(obj);
    this._hideList();
  }

  _setIcon(objectId) {
    if (String(this._prevObjectId) !== String(objectId)) {
      this._setActiveIcon(objectId);
      this._setDefaultIcon(this._prevObjectId);
      this._prevObjectId = objectId;
    }
  }

  _hideList() {
    if (this._list) {
      this._list.classList.add('hide');
    }
  }

  _showList() {
    if (this._list) {
      this._list.classList.remove('hide');
    }
  }

  _centerBalloon(evt) {
    let elem = evt.target.closest('.points__item');
    if (elem) {
      const objectId = elem.dataset.id;
      this.centerById(objectId);
    }
  }

  _focus(objectId) {
    let obj = this._objectManager.objects.getById(objectId);
    let coords = obj.geometry.coordinates.map(Number);

    this._setMaximumZoom();

    this._myMap.panTo(coords, {
      useMapMargin: true,
      checkZoomRange: true
    });

  }

  _setMaximumZoom() {
    let mapZoom = this._myMap.getZoom();
    if (mapZoom < MAX_ZOOM) {
      this._myMap.setZoom(MAX_ZOOM);
    }
  }

  _setActiveIcon(objectId) {
    this._objectManager.objects.setObjectOptions(objectId, {
      preset: 'fixmobile#cdek-active'
    });
  }

  _setDefaultIcon(objectId) {
    this._objectManager.objects.setObjectOptions(objectId, {
      preset: 'fixmobile#cdek'
    });
  }

  _setList() {
    if (this._hasPoints() && this._hasCustomBaloon() && this._isDesktop() && this._tabs !== null) {
      return this._points.getPoints().closest('.tabs__container');
    }

    return null;
  }

  _hasPoints() {
    return this._container.dataset.points === 'true';
  }

  _hasFilterList() {
    return this._container.dataset.filterList === 'true';
  }

  _hasFilterSelect() {
    return this._container.dataset.filterSelect === 'true';
  }

  _hasCustomBaloon() {
    return this._container.dataset.baloon === 'custom';
  }

  _getBuild() {
    return this._container.dataset.build;
  }

  _isDesktop() {
    return window.matchMedia('(min-width: 992px)').matches;
  }

  _isMapWithMargin() {
    return this._hasPoints() && this._isDesktop();
  }
}
